import React, {useState, useEffect} from 'react';
import {db} from '../config/firebase';
import {fetchFollowing, fetchUserNames, fetchRecipes, handleLike} from '../utils/dbUtils';
import RecipeCard from './RecipeCard';
import UserSearchBar from './UserSearchBar';
import RecommendedUsers from './RecommendedUsers';
import './CommunityFeed.css';
import {useAuth} from '../Auth/AuthContext';
import addIcon from '../Icons/post recipe gray.png';
import {useNavigate} from 'react-router-dom';
import {addRecipeToFavorites, removeRecipeFromFavorites, getFavoriteRecipes} from '../RecipeDB/recipeService';
import RecipeCardSkeleton from './RecipeCardSkeleton';
import useRecommendedUsers from '../hooks/useRecommendedUsers';
import { collection, query, orderBy, limit, startAfter, getDocs } from 'firebase/firestore';
import AdSlot from '../Monetization/AdSlot';

const PAGE_SIZE = 12;

const CommunityFeed = () => {
    const {userId} = useAuth();
    const navigate = useNavigate();
    const [recipes, setRecipes]         = useState([]);
    const [loading, setLoading]         = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [lastDoc, setLastDoc]         = useState(null);
    const [hasMore, setHasMore]         = useState(true);
    const [userNames, setUserNames]     = useState({});
    const [favoriteRecipesIds, setFavoriteRecipesIds] = useState([]);

    const { recommendedUsers, loading: loadingRecs } = useRecommendedUsers();

    // Carga inicial unificada — todo en paralelo, un solo loading
    useEffect(() => {
        if (!userId) return;
        const init = async () => {
            setLoading(true);
            setRecipes([]);
            try {
                // Following, nombres y primera página de recetas en paralelo
                const [followingData, userNamesData, snap] = await Promise.all([
                    fetchFollowing(userId),
                    fetchUserNames(),
                    getDocs(query(
                        collection(db, 'recetas'),
                        orderBy('sharedAt', 'desc'),
                        limit(PAGE_SIZE)
                    )),
                ]);
                setUserNames(userNamesData);
                setLastDoc(snap.docs[snap.docs.length - 1] || null);
                setHasMore(snap.docs.length === PAGE_SIZE);

                if (followingData.length > 0) {
                    // Si sigue a alguien, carga sus recetas (pueden tardar un poco más)
                    const recipesData = await fetchRecipes(followingData, userNamesData);
                    setRecipes(recipesData.length > 0
                        ? recipesData
                        : snap.docs.map(d => ({ id: d.id, ...d.data() }))
                    );
                } else {
                    // Sin following → muestra todas las recetas recientes
                    setRecipes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
                }
            } catch (e) {
                console.error('Error cargando feed:', e);
            } finally {
                setLoading(false);
            }
        };
        init();
    }, [userId]);

    useEffect(() => {
        if (!userId) return;
        const fetchFavorites = async () => {
            const favorites = await getFavoriteRecipes(userId);
            setFavoriteRecipesIds(favorites.map(r => r.id));
       };
        fetchFavorites();
   }, [userId]);

    const handleLikeRecipe = async (recipeId) => {
        try {
            await handleLike(recipeId, userId, setRecipes);
       } catch (error) {
            console.error('Error al dar like a la receta:', error);
       }
   };

    const handleAddToFavorites = async (recipe) => {
        try {
            await addRecipeToFavorites(userId, recipe);
            setFavoriteRecipesIds(prev => [...prev, recipe.id]);
       } catch (error) {
            console.error('Error al guardar la receta en favoritas:', error);
       }
   };

    const handleRemoveFromFavorites = async (recipe) => {
        try {
            await removeRecipeFromFavorites(userId, recipe);
            setFavoriteRecipesIds(prev => prev.filter(id => id !== recipe.id));
       } catch (error) {
            console.error('Error al eliminar la receta de favoritas:', error);
       }
   };

    // Cargar más
    const loadMore = async () => {
        if (!lastDoc || loadingMore) return;
        setLoadingMore(true);
        try {
            const q = query(
                collection(db, 'recetas'),
                orderBy('sharedAt', 'desc'),
                startAfter(lastDoc),
                limit(PAGE_SIZE)
            );
            const snap = await getDocs(q);
            const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setRecipes(prev => [...prev, ...docs]);
            setLastDoc(snap.docs[snap.docs.length - 1] || null);
            setHasMore(snap.docs.length === PAGE_SIZE);
        } catch (e) {
            console.error('Error cargando más recetas:', e);
        } finally {
            setLoadingMore(false);
        }
    };

    return (
        <div className="community-feed-container">
            <UserSearchBar />

            {/* Recomendaciones: independiente del feed loading */}
            {!loadingRecs && (
                <RecommendedUsers
                    users={recommendedUsers}
                    title="Cocineros que te pueden interesar"
                />
            )}

            <div className="feed">
                {/* Skeletons mientras carga — un único flag */}
                {loading
                    ? Array(8).fill(0).map((_, i) => (
                        <RecipeCardSkeleton key={`sk-${i}`} />
                      ))
                    : recipes.map((recipe, idx) => (
                        <React.Fragment key={recipe?.id || recipe?.objectID || idx}>
                            <RecipeCard
                                recipe={recipe}
                                userId={userId}
                                userNames={userNames}
                                handleLike={handleLikeRecipe}
                                handleAddToFavorites={handleAddToFavorites}
                                handleRemoveFromFavorites={handleRemoveFromFavorites}
                                favoriteRecipesIds={favoriteRecipesIds}
                            />
                            {/* Anuncio cada 5 tarjetas */}
                            {(idx + 1) % 5 === 0 && (
                                <AdSlot
                                    slotId="feedCard"
                                    format="rectangle"
                                    style={{ margin: '8px 0' }}
                                />
                            )}
                        </React.Fragment>
                      ))
                }
            </div>

            {/* Empty state — solo cuando la carga terminó y no hay nada */}
            {!loading && recipes.length === 0 && (
                <div className="feed-empty">
                    <span>🍽️</span>
                    <p>Aún no hay recetas. ¡Sé el primero en compartir!</p>
                </div>
            )}

            {!loading && hasMore && (
                <div style={{display:'flex', justifyContent:'center', margin:'18px 0'}}>
                    <button
                        className="ver-mas-btn"
                        onClick={loadMore}
                        disabled={loadingMore}
                    >
                        {loadingMore ? 'Cargando...' : 'Ver más recetas'}
                    </button>
                </div>
            )}

            <button
                className="circular-green-btn"
                aria-label="Crear receta"
                onClick={() => navigate('/add-recipe')}
            >
                <img src={addIcon} alt="Crear receta" className="add-recipe-icon" />
            </button>
        </div>
    );
};

export default CommunityFeed;